import type { ComputedVoucher, ComputedLine, ComputedCell } from '../../types/lohnBuchungsbeleg';
import { fmtMoney } from '../../services/lohnBuchungsbelegApi';

interface Props {
    voucher: ComputedVoucher;
    editable: boolean;
    onCellClick: (line: ComputedLine, cell: ComputedCell) => void;
    /** Hide rows whose every cost-center cell is (near) zero. */
    hideEmpty?: boolean;
}

/** A line is "empty" when no cost-center cell carries a value. */
function isEmptyLine(line: ComputedLine): boolean {
    return line.cells.every(c => Math.abs(c.final) < 0.005);
}

/** The voucher matrix: booking lines × cost-center columns, with totals and checksum. */
export function VoucherGrid({ voucher, editable, onCellClick, hideEmpty }: Props) {
    const ccs = voucher.costCenters;
    const lines = hideEmpty ? voucher.lines.filter(l => !isEmptyLine(l)) : voucher.lines;

    return (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full border-collapse text-xs">
                <thead>
                    <tr className="bg-slate-100 text-slate-600">
                        <th className="sticky left-0 z-10 bg-slate-100 px-2 py-1.5 text-left font-semibold">Beschreibung</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Soll</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Haben</th>
                        {ccs.map(cc => (
                            <th key={cc.code} className="min-w-[92px] whitespace-nowrap px-3 py-1.5 text-right font-semibold" title={cc.label ?? undefined}>{cc.code}</th>
                        ))}
                        <th className="min-w-[150px] whitespace-nowrap px-2 py-1.5 text-left font-semibold">Text</th>
                    </tr>
                </thead>
                <tbody>
                    {lines.map(line => (
                        <tr key={line.templateLineId} className="border-t border-slate-100 hover:bg-slate-50/60">
                            <td className="sticky left-0 z-10 max-w-[280px] truncate bg-white px-2 py-1 text-slate-700" title={line.beschreibung}>
                                {line.beschreibung}
                            </td>
                            <td className="px-2 py-1 text-right font-mono text-slate-500">{line.soll ?? ''}</td>
                            <td className="px-2 py-1 text-right font-mono text-slate-500">{line.haben ?? ''}</td>
                            {line.cells.map(cell => {
                                const overridden = cell.override !== null;
                                const zero = Math.abs(cell.final) < 0.005;
                                return (
                                    <td
                                        key={cell.costCenterCode}
                                        onClick={editable ? () => onCellClick(line, cell) : undefined}
                                        title={overridden ? `Override durch ${cell.overriddenBy ?? ''}: ${cell.comment ?? ''}` : undefined}
                                        className={[
                                            'min-w-[92px] whitespace-nowrap px-3 py-1 text-right font-mono tabular-nums',
                                            overridden ? 'bg-amber-100 font-semibold text-amber-800 ring-1 ring-inset ring-amber-300' : (zero ? 'text-slate-300' : 'text-slate-700'),
                                            editable ? 'cursor-pointer hover:bg-amber-50' : '',
                                        ].join(' ')}
                                    >
                                        {fmtMoney(cell.final) || (editable ? '·' : '')}
                                    </td>
                                );
                            })}
                            <td className="min-w-[150px] whitespace-nowrap px-2 py-1 text-slate-400">{line.text}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold text-slate-700">
                        <td className="sticky left-0 z-10 bg-slate-50 px-2 py-1.5">Total</td>
                        <td /><td />
                        {ccs.map(cc => (
                            <td key={cc.code} className="min-w-[92px] whitespace-nowrap px-3 py-1.5 text-right font-mono tabular-nums">
                                {fmtMoney(voucher.columnTotals[cc.code] ?? 0)}
                            </td>
                        ))}
                        <td />
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}
